/**
 * @module geo
 */

import { Store } from "redux";
import watch from "redux-watch";
import { Observable } from "rxjs/Observable";
import { fromPromise } from "rxjs/observable/fromPromise";

import {
  addToSelectionCreatorP,
  removeFromSelectionCreatorP,
  resetSelectionCreatorP
} from "./actions/creators";
import { Watch } from "./decorators/watch";
import { AppStore } from "./store/app";
import { stateOfSelection } from "./store/selectors";
import { Feature, FeatureSelection } from "./types";
import { Watcher } from "./watcher";

/**
 * Entry point for management of features
 *
 * @since v 2.0.2
 */
export interface Selection extends Watcher {}

@Watch({})
export class Selection {
  /**
   * boservers bound to this map
   */
  public observers: any;

  /**
   * liste of selected features
   */
  public features: Feature[] = [];

  /**
   * default path for listening properties
   * @type {string}
   */
  public path: string = "features";

  /**
   * store bound to this map
   */
  public store: Store<AppStore> | null = null;

  /**
   *
   * @private
   */
  constructor() {
    this.observers = {};
  }

  /**
   * connect selection to store
   * @internalapi
   * @private
   */
  public connect(store: Store<AppStore>) {
    const w = watch(() => stateOfSelection(store.getState()));

    store.subscribe(
      w((newVal: Feature[], oldVal: Selection[], objectPath: string) => {
        this.features = newVal;
      })
    );

    this.store = store;
  }

  /**
   * Reset the selection on layers or all the current selection
   * @param string[] layers
   */
  public reset(layers?: string[]): Observable<any> {
    if (this.store == null) {
      throw new Error("store is null");
    }
    return fromPromise(resetSelectionCreatorP(layers, this.store.dispatch));
  }

  /**
   * Add some features to current selection
   *
   * ```js
   *
   * geoApp.selection.add([
   *     {
   *       id: '21',
   *       entryKey: 'xxx', // key of mapentry
   *       layerType: 'layer',
   *       label: 'Mon objet'
   *     },
   *     {
   *       id: '21',
   *       layerKey: 'xxx!geom', // id of data source
   *       layerType: 'layer',
   *       label: 'Mon objet'
   *     },
   *     {
   *       id: 'tmp/5',
   *       layerKey: 1,
   *       layerType: 'draw' // temporary draw layer
   *     }],
   * true);
   *
   * ```
   * 
   *  see [Selection Example](showcase/selection/selection-example)
   */
  public add(features: FeatureSelection[], replace: boolean): Observable<any> {
    if (this.store == null) {
      throw new Error("store is null");
    }
    return fromPromise(
      addToSelectionCreatorP({ features, replace }, this.store.dispatch)
    );
  }

  /**
   * Remove some features from current selection
   */
  public remove(features: Feature[]): Observable<any> {
    if (this.store == null) {
      throw new Error("store is null");
    }
    return fromPromise(
      removeFromSelectionCreatorP({ features }, this.store.dispatch)
    );
  }
}
